/* v2.4 2026-10-14 - print recommendations: scroll rails, the lazy related row and the lazy artist link check
   (sections/aa-print-recs.liquid). The subject row and the artist link are server-rendered; the related row is
   fetched, and an artist link marked data-aa-prr-check is hidden when its own search finds no other work. */
(function () {
  'use strict';
  if (!('customElements' in window) || window.customElements.get('aa-print-recs')) return;

  var motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function on(el, type, fn, opts, bag) {
    var o = opts || {};
    if (bag && bag.signal) o.signal = bag.signal;
    el.addEventListener(type, fn, o);
  }

  function isOff(btn) {
    return btn.getAttribute('aria-disabled') === 'true';
  }

  function setOff(btn, off) {
    if (btn) btn.setAttribute('aria-disabled', off ? 'true' : 'false');
  }

  /* One horizontal rail: prev/next buttons, progress bar, overflow flag. */
  function setupRail(rail, bag) {
    if (!rail || !bag || rail.hasAttribute('data-aa-prr-ready')) return;
    var track = rail.querySelector('[data-aa-prr-track]');
    if (!track) return;
    rail.setAttribute('data-aa-prr-ready', '');
    var prev = rail.querySelector('[data-aa-prr-prev]');
    var next = rail.querySelector('[data-aa-prr-next]');
    var bar = rail.querySelector('[data-aa-prr-bar]');
    var frame = 0;

    function queue() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    function update() {
      frame = 0;
      var max = track.scrollWidth - track.clientWidth;
      var over = max > 2;
      var was = rail.hasAttribute('data-overflow');
      var x = Math.abs(track.scrollLeft);
      if (over) rail.setAttribute('data-overflow', '');
      else rail.removeAttribute('data-overflow');
      setOff(prev, !over || x <= 2);
      setOff(next, !over || x >= max - 2);
      if (bar) {
        var ratio = over ? Math.min(1, (x + track.clientWidth) / track.scrollWidth) : 1;
        bar.style.transform = 'scaleX(' + ratio.toFixed(4) + ')';
      }
      /* A centred short row switches to start alignment once it overflows: measure again. */
      if (over !== was) queue();
    }

    function step(dir) {
      var rtl = window.getComputedStyle(track).direction === 'rtl';
      var dist = Math.max(track.clientWidth * 0.85, 160) * dir * (rtl ? -1 : 1);
      var smooth = !(motion && motion.matches);
      if (typeof track.scrollBy === 'function') {
        try {
          track.scrollBy({ left: dist, behavior: smooth ? 'smooth' : 'auto' });
          return;
        } catch (e) { /* older engines: plain assignment below */ }
      }
      track.scrollLeft += dist;
    }

    if (prev) on(prev, 'click', function () { if (!isOff(prev)) step(-1); }, null, bag);
    if (next) on(next, 'click', function () { if (!isOff(next)) step(1); }, null, bag);
    on(track, 'scroll', queue, { passive: true }, bag);
    on(track, 'load', queue, { capture: true }, bag);
    if ('ResizeObserver' in window) {
      var ro = new window.ResizeObserver(queue);
      ro.observe(track);
      bag.observers.push(ro);
    } else {
      on(window, 'resize', queue, { passive: true }, bag);
    }
    update();
  }

  /* Pool markup rendered by the same section file: <ul data-aa-prr-pool> with <li data-pid data-type>.
     The search pool also carries data-aa-prr-total (all results) and data-title on each item. */
  function readPool(html) {
    var doc = new window.DOMParser().parseFromString(html, 'text/html');
    var ul = doc.querySelector('[data-aa-prr-pool]');
    if (!ul) return null;
    var total = parseInt(ul.getAttribute('data-aa-prr-total'), 10);
    return {
      items: Array.prototype.slice.call(ul.querySelectorAll('li[data-pid]')),
      total: isNaN(total) ? -1 : total
    };
  }

  /* Artist key of a title: the text after the last " by ", lower case (as in the section). */
  function artistKey(title) {
    var s = String(title || '');
    if (s.indexOf(' by ') < 0) return '';
    return s.split(' by ').pop().trim().toLowerCase();
  }

  /* Rotate a list so different artworks start the row on different product pages. */
  function rotate(list, seed) {
    if (list.length < 2) return list;
    var start = seed % list.length;
    return list.slice(start).concat(list.slice(0, start));
  }

  class AaPrintRecs extends HTMLElement {
    connectedCallback() {
      var bag = { signal: null, ctrl: null, observers: [] };
      if ('AbortController' in window) {
        bag.ctrl = new window.AbortController();
        bag.signal = bag.ctrl.signal;
      }
      this._bag = bag;
      var rails = this.querySelectorAll('[data-aa-prr-rail]');
      for (var i = 0; i < rails.length; i++) {
        if (!rails[i].closest('[hidden]')) setupRail(rails[i], bag);
      }
      if (!this._done && (this.getAttribute('data-url') || this.querySelector('[data-aa-prr-check]'))) this._watch();
    }

    disconnectedCallback() {
      var bag = this._bag;
      this._bag = null;
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      if (!bag) return;
      if (bag.ctrl) bag.ctrl.abort();
      for (var i = 0; i < bag.observers.length; i++) bag.observers[i].disconnect();
      var rails = this.querySelectorAll('[data-aa-prr-ready]');
      for (var j = 0; j < rails.length; j++) rails[j].removeAttribute('data-aa-prr-ready');
    }

    /* Fetch the related row and run the artist check only when the section comes near the viewport. */
    _watch() {
      var self = this;
      if (!('IntersectionObserver' in window)) {
        this._load();
        return;
      }
      this._io = new window.IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            if (self._io) {
              self._io.disconnect();
              self._io = null;
            }
            self._load();
            break;
          }
        }
      }, { rootMargin: '0px 0px 600px 0px' });
      this._io.observe(this);
    }

    /* GET a pool; resolves to null on any failure (the row then stays hidden). */
    _get(url) {
      if (!url) return Promise.resolve(null);
      var opts = { credentials: 'same-origin' };
      if (this._bag && this._bag.signal) opts.signal = this._bag.signal;
      return window.fetch(url, opts)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(readPool)
        .catch(function () { return null; });
    }

    _load() {
      var self = this;
      if (!this._bag || this._busy || !window.fetch || !window.DOMParser || !window.Promise) return;
      this._busy = true;
      var jobs = [];
      if (this.getAttribute('data-url')) jobs.push(this._loadRecs());
      var check = this.querySelector('[data-aa-prr-check]');
      if (check) jobs.push(this._checkArtist(check));
      window.Promise.all(jobs).then(function () {
        self._done = true;
        self._busy = false;
        /* Nothing to show (no row, no link, no editor note): drop the empty section spacing. */
        if (self.isConnected && !self.querySelector('.aa-prr__row:not([hidden]), .aa-prr__note')) {
          self.style.display = 'none';
        }
      }, function () {
        self._busy = false;
      });
    }

    /* Artist link check: the link's own search (first 10 results). Hidden only when every result was read
       and none is another work of the type by the artist; kept on any failure or unexpected response. */
    _checkArtist(row) {
      var self = this;
      var key = String(row.getAttribute('data-aa-prr-artist') || '').trim().toLowerCase();
      var type = this.getAttribute('data-type') || '';
      var pid = this.getAttribute('data-pid') || '';
      return this._get(row.getAttribute('data-aa-prr-check')).then(function (pool) {
        if (!pool || pool.total < 0 || !key || !self.isConnected || !self._bag) return;
        if (pool.total > pool.items.length) return;
        for (var i = 0; i < pool.items.length; i++) {
          var it = pool.items[i];
          var id = it.getAttribute('data-pid');
          if (!id || id === pid) continue;
          if (type && it.getAttribute('data-type') !== type) continue;
          if (artistKey(it.getAttribute('data-title')) === key) return;
        }
        row.hidden = true;
        if (window.Shopify && window.Shopify.designMode) {
          var note = document.createElement('p');
          note.className = 'aa-prr__note';
          note.textContent = 'Artist link hidden: the store search finds no other work by ' + row.getAttribute('data-aa-prr-artist') + '.';
          row.parentNode.insertBefore(note, row);
        }
      });
    }

    /* Related row: related products of the type; the fallback pool when none are left. */
    _loadRecs() {
      var self = this;
      var type = this.getAttribute('data-type') || '';
      var seed = parseInt(this.getAttribute('data-pid'), 10) || 0;
      var seen = {};
      (this.getAttribute('data-exclude') || '').split(',').forEach(function (id) {
        id = id.trim();
        if (id) seen[id] = true;
      });

      function pick(items, skip, max, out) {
        for (var k = 0; k < items.length && out.length < max; k++) {
          var id = items[k].getAttribute('data-pid');
          if (!id || skip[id]) continue;
          if (type && items[k].getAttribute('data-type') !== type) continue;
          skip[id] = true;
          out.push(items[k]);
        }
        return out;
      }

      return this._get(this.getAttribute('data-url'))
        .then(function (pool) {
          if (!self.isConnected || !self._bag) return null;

          var max = parseInt(self.getAttribute('data-max'), 10) || 8;
          var topup = self.getAttribute('data-topup') === 'true';
          var out = pool ? pick(pool.items, seen, max, []) : [];
          var fb = self.getAttribute('data-fb-url');
          if (fb && (!out.length || (topup && out.length < max))) {
            return self._get(fb).then(function (fbPool) {
              if (fbPool && self.isConnected && self._bag) pick(rotate(fbPool.items, seed), seen, max, out);
              self._fillRecs(out);
            });
          }
          self._fillRecs(out);
          return null;
        });
    }

    _render(row, list) {
      var track = row.querySelector('[data-aa-prr-track]');
      if (!track) return false;
      /* Card image sizes from this page's settings (a pool rendered by file name carries the defaults).
         Set on the parsed node, before it joins the page, so the first image request already uses it. */
      var tpl = this.getAttribute('data-card-sizes') || '';
      var frag = document.createDocumentFragment();
      for (var n = 0; n < list.length; n++) {
        var img = tpl ? list[n].querySelector('img.aa-pcard__img') : null;
        if (img) img.setAttribute('sizes', tpl.split('#f').join(list[n].getAttribute('data-f') || '1'));
        frag.appendChild(document.importNode(list[n], true));
      }
      track.appendChild(frag);
      return true;
    }

    _fillRecs(list) {
      if (!this.isConnected || !this._bag || !list.length) return;
      var row = this.querySelector('[data-aa-prr-recs]');
      if (!row || !this._render(row, list)) return;
      row.hidden = false;
      setupRail(row.querySelector('[data-aa-prr-rail]'), this._bag);
      this.dispatchEvent(new CustomEvent('aa-prr:loaded', { bubbles: true, detail: { row: 'related', count: list.length } }));
    }
  }

  window.customElements.define('aa-print-recs', AaPrintRecs);
})();
